import React from "react";
import { Alert, Badge, Card, Spinner } from "flowbite-react";
import { useQuery } from "react-query";
import {
  HiOutlineExclamation,
  HiOutlineExternalLink,
  HiOutlineInformationCircle,
} from "react-icons/hi";

import SubTitle from "../../components/SubTitle";
import Button from "../../components/Button";
import { getMemberSubscriptions } from "../../api/memberSubscriptions/actions";
import {
  CurrentMemberSubscription,
  LiveChargebeeSubscription,
  MemberSubscriptionRow,
  MemberSubscriptionsResponse,
  SubscriptionDrift,
} from "../../api/memberSubscriptions/types";
import { getSubscriptionStatusColor } from "../../utils/subscriptionStatusColor";
import { formatDateTime } from "../../../utils/date";
import {
  memberSubscriptionsStrings as S,
  formatMessage,
} from "./MemberSubscriptionsTab.strings";

interface MemberSubscriptionsTabProps {
  customerId: string;
}

/** Statuses (DS or live Chargebee) that mean the subscription has ended. */
const CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);

/** Statuses (DS or live Chargebee) that mean the subscription is in trial. */
const TRIAL_STATUSES = new Set(["in_trial", "trialing"]);

/** Legacy (Chargify-era) records: frozen status, no Chargebee link. */
const LEGACY_PLATFORMS = new Set(["legacy", "Chargify"]);

/** Chargebee failures that mean the live read was attempted and missed. */
const UNREACHABLE_REASONS = new Set(["timeout", "error"]);

const DRIFT_FIELDS: Array<keyof SubscriptionDrift> = [
  "status",
  "plan",
  "customer",
];

/** An ISO-8601 date through the shared ABO formatter; "—" when absent. */
const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return S.none;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? S.none : formatDateTime(date);
};

/** Chargebee `unit_price` is in minor units of the currency. */
const formatPrice = (live: LiveChargebeeSubscription): string => {
  if (live.unitPrice === null || live.unitPrice === undefined) {
    return S.none;
  }
  const major = live.unitPrice / 100;
  let price: string;
  try {
    price = live.currencyCode
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: live.currencyCode,
        }).format(major)
      : major.toFixed(2);
  } catch {
    price = major.toFixed(2);
  }
  if (!live.billingPeriodUnit) {
    return price;
  }
  const period =
    live.billingPeriod && live.billingPeriod > 1
      ? formatMessage(S.current.billingPeriodMultiple, {
          count: live.billingPeriod,
          unit: live.billingPeriodUnit,
        })
      : formatMessage(S.current.billingPeriodSingle, {
          unit: live.billingPeriodUnit,
        });
  return formatMessage(S.current.priceWithPeriod, { price, period });
};

/**
 * The cancellation field of the current card. The live Chargebee status leads
 * (ADR-001): `non_renewing` → "Cancels on", `cancelled` → "Cancelled on", both
 * with `cancelledAt`. Without a live block, the DS `cancellationScheduledAt`
 * ("Cancelled on" when the DS record is already cancelled).
 */
const cancellationField = (
  current: CurrentMemberSubscription
): { label: string; value: string } => {
  const { live } = current;
  const liveStatus = (live?.status ?? "").toLowerCase();
  if (live && liveStatus === "non_renewing") {
    return {
      label: S.current.cancelsOn,
      value: formatDate(live.cancelledAt ?? current.cancellationScheduledAt),
    };
  }
  if (live && CANCELLED_STATUSES.has(liveStatus)) {
    return { label: S.current.cancelledOn, value: formatDate(live.cancelledAt) };
  }
  if (!live && current.cancellationScheduledAt) {
    return {
      label: CANCELLED_STATUSES.has(current.status.toLowerCase())
        ? S.current.cancelledOn
        : S.current.cancelsOn,
      value: formatDate(current.cancellationScheduledAt),
    };
  }
  return { label: S.current.cancellation, value: S.none };
};

/** The status shown for the current subscription: live first, then DS. */
const displayStatus = (current: CurrentMemberSubscription): string =>
  current.live?.status || current.status;

/**
 * The "Current period" value of the current card: the live term start and the
 * live (else DS) term end. Chargebee has no billing term during a trial, so an
 * in-trial subscription with no term dates shows its trial start and end
 * instead, labelled as the trial. Every other case is unchanged.
 */
const currentPeriodValue = (current: CurrentMemberSubscription): string => {
  const { live } = current;
  const periodStart = live?.currentTermStart ?? null;
  const periodEnd = live?.currentTermEnd ?? current.currentTermEnd;
  if (!periodStart && !periodEnd) {
    const trialStart = live?.trialStart ?? current.trialStart;
    const trialEnd = live?.trialEnd ?? current.trialEnd;
    const inTrial = TRIAL_STATUSES.has(displayStatus(current).toLowerCase());
    if (inTrial && (trialStart || trialEnd)) {
      return formatMessage(S.current.trialPeriodRange, {
        start: formatDate(trialStart),
        end: formatDate(trialEnd),
      });
    }
  }
  return formatMessage(S.current.periodRange, {
    start: formatDate(periodStart),
    end: formatDate(periodEnd),
  });
};

const SectionCard: React.FC<{
  heading: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ heading, children, testId }) => (
  <Card {...(testId ? { "data-testid": testId } : {})}>
    <div className="pb-3 mb-3 border-b border-gray-100">
      <SubTitle>{heading}</SubTitle>
    </div>
    {children}
  </Card>
);

const Field: React.FC<{
  label: string;
  value: string;
  testId?: string;
}> = ({ label, value, testId }) => (
  <div {...(testId ? { "data-testid": testId } : {})}>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

const ExternalLink: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:underline"
  >
    {label}
    <HiOutlineExternalLink className="h-4 w-4" aria-hidden />
  </a>
);

const PlanHeading: React.FC<{ row: MemberSubscriptionRow }> = ({ row }) => (
  <div className="min-w-0">
    <p className="font-semibold text-neutral-800">
      {row.planName ?? S.current.unknownPlan}
    </p>
    <p className="font-mono text-xs text-gray-500 break-all">
      {row.itemPriceId ?? S.none}
    </p>
    {row.planSource === "chargebee" && (
      <p
        className="text-xs text-gray-500"
        data-testid="subscriptions-plan-source-chargebee"
      >
        {S.planFromChargebee}
      </p>
    )}
  </div>
);

const Notices: React.FC<{ data: MemberSubscriptionsResponse }> = ({ data }) => {
  const { current, chargebee, hasSubscription } = data;
  const reason = chargebee.available ? undefined : chargebee.reason;
  const driftFields = current?.drift
    ? DRIFT_FIELDS.filter((field) => current.drift?.[field])
    : [];
  const currentCancelled = current
    ? CANCELLED_STATUSES.has(displayStatus(current).toLowerCase())
    : false;
  const showResignupHint = !hasSubscription || currentCancelled;

  return (
    <>
      {reason && UNREACHABLE_REASONS.has(reason) && (
        <Alert
          color="warning"
          icon={HiOutlineExclamation}
          data-testid="subscriptions-notice-unavailable"
        >
          {S.notices.chargebeeUnavailable}
        </Alert>
      )}
      {reason === "not_found" && (
        <Alert
          color="warning"
          icon={HiOutlineExclamation}
          data-testid="subscriptions-notice-not-found"
        >
          {S.notices.chargebeeNotFound}
        </Alert>
      )}
      {driftFields.length > 0 && (
        <Alert
          color="warning"
          icon={HiOutlineExclamation}
          data-testid="subscriptions-notice-drift"
        >
          {formatMessage(S.notices.drift, {
            fields: driftFields
              .map((field) => S.notices.driftFields[field])
              .join(S.notices.driftFieldSeparator),
          })}
        </Alert>
      )}
      {showResignupHint && (
        <Alert
          color="info"
          icon={HiOutlineInformationCircle}
          data-testid="subscriptions-notice-resignup"
        >
          {S.notices.resignupHint}
        </Alert>
      )}
    </>
  );
};

const CurrentCard: React.FC<{
  current: CurrentMemberSubscription;
  customerUrl: string | null;
}> = ({ current, customerUrl }) => {
  const { live } = current;
  const status = displayStatus(current);
  const cancellation = cancellationField(current);

  return (
    <SectionCard heading={S.current.heading} testId="subscriptions-current">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PlanHeading row={current} />
        <Badge
          color={getSubscriptionStatusColor(status)}
          size="sm"
          className="w-fit"
          data-testid="subscriptions-current-status"
        >
          {status}
        </Badge>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field
          label={S.current.trialEnd}
          value={formatDate(live?.trialEnd ?? current.trialEnd)}
        />
        <Field
          label={S.current.currentPeriod}
          value={currentPeriodValue(current)}
          testId="subscriptions-current-period"
        />
        <Field
          label={S.current.nextBilling}
          value={formatDate(live?.nextBillingAt)}
          testId="subscriptions-current-next-billing"
        />
        <Field
          label={cancellation.label}
          value={cancellation.value}
          testId="subscriptions-current-cancellation"
        />
        <Field
          label={S.current.created}
          value={formatDate(live?.createdAt ?? current.createdAt)}
        />
        {live && (
          <Field
            label={S.current.price}
            value={formatPrice(live)}
            testId="subscriptions-current-price"
          />
        )}
      </div>
      {(current.adminUrl || customerUrl) && (
        <div className="mt-4 flex flex-wrap gap-4">
          {current.adminUrl && (
            <ExternalLink
              href={current.adminUrl}
              label={S.current.openSubscription}
            />
          )}
          {customerUrl && (
            <ExternalLink href={customerUrl} label={S.current.openCustomer} />
          )}
        </div>
      )}
    </SectionCard>
  );
};

const HistoryRow: React.FC<{ row: MemberSubscriptionRow }> = ({ row }) => (
  <li
    className="rounded-md border border-gray-100 p-3"
    data-testid="subscriptions-history-row"
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <PlanHeading row={row} />
      <div className="flex flex-wrap items-center gap-2">
        {LEGACY_PLATFORMS.has(row.billingPlatform) && (
          <Badge color="gray" size="sm" className="w-fit">
            {S.history.legacy}
          </Badge>
        )}
        <Badge
          color={getSubscriptionStatusColor(row.status)}
          size="sm"
          className="w-fit"
        >
          {row.status}
        </Badge>
        <span className="text-xs text-gray-500">
          {S.history.statusAsRecorded}
        </span>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Field label={S.history.created} value={formatDate(row.createdAt)} />
      <Field label={S.history.termEnd} value={formatDate(row.currentTermEnd)} />
      <Field
        label={S.history.cancellation}
        value={formatDate(row.cancellationScheduledAt)}
      />
    </div>
    {row.adminUrl && (
      <div className="mt-3">
        <ExternalLink href={row.adminUrl} label={S.history.openSubscription} />
      </div>
    )}
  </li>
);

/**
 * Read-only Subscriptions tab of Customer Information (ADR-002: no billing
 * actions, only Chargebee links built by DS). Loads
 * `GET /v1/abo/customers/:id/subscriptions` on mount: the DS current record
 * enriched with one live Chargebee read, plus the DS history (ADR-001).
 */
const MemberSubscriptionsTab: React.FC<MemberSubscriptionsTabProps> = ({
  customerId,
}) => {
  const query = useQuery(
    ["member-subscriptions", customerId],
    () => getMemberSubscriptions(customerId),
    {
      enabled: Boolean(customerId),
      // One live Chargebee read per tab open: no refetch on window focus, and
      // no automatic retries (the app QueryClient defaults to 3 with backoff,
      // which would hold the spinner ~7 s and repeat the read). A failure
      // shows the load-error notice at once; the operator retries by hand.
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const data = query.data;

  return (
    <div className="flex flex-col gap-6" data-testid="member-subscriptions-tab">
      <p className="text-sm text-gray-500">{S.readOnlyCaption}</p>

      {query.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}

      {query.isError && (
        <Alert
          color="failure"
          icon={HiOutlineExclamation}
          data-testid="subscriptions-load-error"
        >
          <div className="flex flex-wrap items-center gap-4">
            <span>{S.loadError}</span>
            <Button
              variant="LIGHT"
              size="xs"
              isProcessing={query.isFetching}
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              {S.retry}
            </Button>
          </div>
        </Alert>
      )}

      {data && (
        <>
          <Notices data={data} />

          {!data.hasSubscription ? (
            <p
              className="text-sm text-gray-500"
              data-testid="subscriptions-empty"
            >
              {S.empty}
            </p>
          ) : data.current ? (
            <CurrentCard
              current={data.current}
              customerUrl={data.chargebeeCustomerUrl}
            />
          ) : (
            <p className="text-sm text-gray-500">{S.current.noCurrent}</p>
          )}

          {data.history.length > 0 && (
            <SectionCard
              heading={formatMessage(S.history.heading, {
                count: data.history.length,
              })}
              testId="subscriptions-history"
            >
              <ul className="flex flex-col gap-3">
                {data.history.map((row, index) => (
                  // DS can hold several records with one subscription id.
                  <HistoryRow key={`${row.subscriptionId}-${index}`} row={row} />
                ))}
              </ul>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
};

export default MemberSubscriptionsTab;
